/**
 * Iter 34 — Admin Dashboard.
 *
 * School-wide snapshot:
 *   - top-row metric cards: providers · students · today's sessions · health
 *   - "Unavailable today" list
 *   - "Health hotspots" — providers with the most open issues
 *
 * FP3-120 — Steady-state cache. Dashboard now uses React Query with
 * `useAdminDashboard(params)`; revisit renders immediately from the
 * cached snapshot and only refetches when stale (2 min) or when
 * the Refresh chip is clicked. Relevant mutations
 * (`invalidateAfterMutation.studentEdited / providerAssignmentChanged
 * / sessionLogged / scheduleChanged / providerHealthChanged /
 * schoolChanged`) invalidate this cache so the dashboard stays fresh
 * after writes.
 */
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, GraduationCap, CalendarBlank, Heartbeat, Warning, ArrowRight, UserCircleMinus } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import AdminShell from "./AdminShell";
import { useAuth } from "@/lib/auth";
import { useAdminDashboard, prefetchCommonAdminDatasets } from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
// FP3-053 — Super Admin context awareness. Shows "Viewing: …" in
// the dashboard subtitle so the operator always knows which org /
// school the metrics reflect.
import { useSuperAdminContext } from "@/lib/useSuperAdminContext";

function MetricCard({ label, value, sub, icon: Icon, accent = "default", testid, onClick }) {
  const accents = {
    default: "border-[#E5E3DB] bg-white",
    green: "border-[#1E5A3F]/20 bg-[#E1EAE4]/30",
    amber: "border-[#9C6B2E]/30 bg-[#FDF8F3]",
    red: "border-[#B85C5C]/25 bg-[#FBEFEC]/60",
    slate: "border-[#3F4756]/20 bg-[#3F4756]/5",
  };
  const iconColor = {
    default: "text-[#6B7280]",
    green: "text-[#1E5A3F]",
    amber: "text-[#9C6B2E]",
    red: "text-[#B85C5C]",
    slate: "text-[#3F4756]",
  }[accent];
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border ${accents[accent]} p-4 transition-all hover:shadow-sm ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">{label}</p>
        <Icon size={16} weight="duotone" className={iconColor} />
      </div>
      <p className="text-3xl font-bold text-[#1F2937] mt-2 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-[#6B7280] mt-1">{sub}</p>}
    </button>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  // FP3-053 — Active org/school context (super_admin only). Resolves
  // to empty strings for org_admin / admin so the subtitle just
  // shows the default copy.
  const { isSuperAdmin, orgId, orgName, schoolName, schoolId } = useSuperAdminContext();
  const contextLabel = (() => {
    if (!isSuperAdmin) return "";
    if (!orgId) return "All Organizations";
    if (orgName && schoolName) return `${orgName} · ${schoolName}`;
    if (orgName) return orgName;
    return ""; // tree not loaded yet — keep subtitle calm
  })();

  // FP3-120 — Steady-state cache via React Query. Dashboard renders
  // instantly from cache on revisit (2 min stale window). Manual
  // Refresh chip forces a fresh authorized request. Cache key
  // embeds authScope + as_org/as_school so a super_admin flipping
  // context gets a separate slot with no cross-org leakage.
  const dashboardParams = useMemo(() => {
    const params = {};
    if (orgId) params.as_org = orgId;
    if (schoolId) params.as_school = schoolId;
    return params;
  }, [orgId, schoolId]);
  const dashboardQuery = useAdminDashboard(dashboardParams);
  const data = dashboardQuery.data || null;
  // "loading" applies only on the very first fetch (no cached data
  // yet); on revisit we render the previous data immediately even
  // while `isFetching` is true.
  const loading = dashboardQuery.isPending;
  const refetching = dashboardQuery.isFetching && !dashboardQuery.isPending;
  const dataUpdatedAt = dashboardQuery.dataUpdatedAt;

  // FP3-119B — Warm the cache for the "likely next page" datasets
  // during idle time after the Dashboard loads. `prefetchQuery`
  // is a no-op if the key is already fresh, so this is cheap on
  // subsequent visits.
  const authCtx = useAuth();
  useEffect(() => {
    if (!authCtx?.user) return;
    // Small setTimeout defers the prefetch until after the initial
    // paint, keeping first-paint cost unchanged.
    const t = setTimeout(() => {
      prefetchCommonAdminDatasets(authCtx);
    }, 250);
    return () => clearTimeout(t);
    // authCtx.user.id is stable per session; prefetch fires once.
  }, [authCtx?.user?.id]);

  return (
    <AdminShell
      title="Dashboard"
      subtitle={
        contextLabel
          ? `Viewing: ${contextLabel} · school-wide snapshot`
          : "School-wide snapshot"
      }
      actions={
        <RefreshChip
          dataUpdatedAt={dataUpdatedAt}
          isFetching={refetching}
          onRefresh={() => dashboardQuery.refetch()}
          testid="dashboard-refresh"
        />
      }
    >
      {loading ? (
        <div className="grid grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : !data ? (
        <p className="text-sm text-[#6B7280]">No data.</p>
      ) : (
        <div className="space-y-4">
          {/* Metric cards row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              testid="metric-providers"
              label="Providers"
              value={data.providers.total}
              sub={`${data.campuses.length} campus${data.campuses.length === 1 ? "" : "es"}`}
              icon={Users}
              onClick={() => navigate("/admin/providers")}
            />
            <MetricCard
              testid="metric-students"
              label="Students with IEPs"
              value={data.students.total}
              sub={(data.students.iep_due_next_14 || data.students.met_due_next_14)
                ? `${data.students.iep_due_next_14} IEP · ${data.students.met_due_next_14} MET due in 14d`
                : "No reviews due in 14 days"}
              icon={GraduationCap}
            />
            <MetricCard
              testid="metric-today-sessions"
              label="Today's sessions"
              value={data.today_sessions.total}
              sub={`${data.today_sessions.completed} completed · ${data.today_sessions.cancelled} cancelled`}
              icon={CalendarBlank}
              accent={data.today_sessions.cancelled > 0 ? "amber" : "default"}
            />
            <MetricCard
              testid="metric-health"
              label="Open health issues"
              value={data.health.total_violations}
              sub={`${data.health.providers_with_issues} provider${data.health.providers_with_issues === 1 ? "" : "s"} affected`}
              icon={Heartbeat}
              accent={data.health.total_violations > 0 ? "red" : "green"}
              onClick={() => navigate("/admin/coverage")}
            />
          </div>

          {/* Unavailable today */}
          <Card className="border-[#E5E3DB]" data-testid="unavailable-today-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1F2937] heading-font flex items-center gap-2">
                <UserCircleMinus size={16} weight="duotone" className="text-[#9C6B2E]" />
                Unavailable today
                {data.providers.unavailable_today.length > 0 && (
                  <Badge className="bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#FDF8F3] text-[10px]">
                    {data.providers.unavailable_today.length}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.providers.unavailable_today.length === 0 ? (
                <p className="text-[12px] text-[#6B7280] italic">All providers available today.</p>
              ) : (
                <div className="space-y-2">
                  {data.providers.unavailable_today.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`unavail-${u.id}`}
                      className="flex items-center justify-between gap-3 bg-[#FDF8F3] border border-[#9C6B2E]/20 rounded-md px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1F2937]">{u.name}</p>
                        <p className="text-[11px] text-[#6B7280]">
                          {u.role} · {u.reason} · through {u.end_date}
                        </p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-[#9C6B2E] font-semibold">Out</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              data-testid="quicklink-shared-calendar"
              onClick={() => navigate("/admin/calendar")}
              className="text-left flex items-center justify-between bg-white border border-[#E5E3DB] rounded-xl p-4 hover:bg-[#FAFAF7] transition-colors"
            >
              <div className="flex items-center gap-3">
                <CalendarBlank size={20} weight="duotone" className="text-[#3F4756]" />
                <div>
                  <p className="text-sm font-semibold text-[#1F2937]">Open Shared Calendar</p>
                  <p className="text-[11px] text-[#6B7280]">See every provider's week side-by-side · filter by campus, role, provider</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-[#6B7280]" />
            </button>
            <button
              data-testid="quicklink-coverage"
              onClick={() => navigate("/admin/coverage")}
              className="text-left flex items-center justify-between bg-white border border-[#E5E3DB] rounded-xl p-4 hover:bg-[#FAFAF7] transition-colors"
            >
              <div className="flex items-center gap-3">
                <Warning size={20} weight="duotone" className="text-[#B85C5C]" />
                <div>
                  <p className="text-sm font-semibold text-[#1F2937]">Coverage & Risk Overview</p>
                  <p className="text-[11px] text-[#6B7280]">{data.health.total_violations} open issues across {data.health.providers_with_issues} provider{data.health.providers_with_issues === 1 ? "" : "s"}</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-[#6B7280]" />
            </button>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
